import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formsApi, patientApi, formDraftApi } from '../../lib/api';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { submitFormResilient } from '../../lib/offlineQueue';
import { evaluateFormula, formatComputed, computeTableRows, computeTotalsRow } from '../../lib/formCalc';
import CalcTableRenderer from '../../components/forms/CalcTableRenderer';
import SignaturePadDialog from '../../components/forms/SignaturePadDialog';
import FillBlankField from '../../components/forms/FillBlankField';
import PaperFillView from '../../components/forms/PaperFillView';
import VoiceNoteDialog from '../../components/voice/VoiceNoteDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Send, Loader2, FileText, Info, PenTool, SeparatorHorizontal, CheckSquare, Clock, Pen, Check, CircleDashed, Trash2, Mic } from 'lucide-react';

export default function FormFillPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectedPatientId = searchParams.get('patientId') || '';
  const taskId = searchParams.get('taskId') || '';
  const preloadDraftId = searchParams.get('draftId') || '';
  const [nextUpdateDue, setNextUpdateDue] = useState('');
  const [recordSchedule, setRecordSchedule] = useState(null);
  const lastPrefillRef = useRef({});
  const [form, setForm] = useState(null);
  const [rawForm, setRawForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState(preselectedPatientId);
  // Assigned aide for this submission. Optional — auto-populated from the
  // current user (if aide) or from the patient's care_log assignment.
  const [aideId, setAideId] = useState('');
  const [aides, setAides] = useState([]);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [taskMarkedInProgress, setTaskMarkedInProgress] = useState(false);
  const [signingFieldId, setSigningFieldId] = useState(null); // open the SignaturePadDialog for this field
  // Auto-save state — "idle" | "saving" | "saved" | "error".  Persist the
  // draft id so subsequent saves update (don't re-create) the same row.
  const [draftId, setDraftId] = useState(preloadDraftId);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const saveTimerRef = useRef(null);
  const skipNextAutoSaveRef = useRef(true); // skip the very first change (hydration)
  const draftIdRef = useRef(preloadDraftId);
  // Render mode — only shown when the form has a paper layout designed.
  // Persists in localStorage so a user's choice sticks across fills.
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('savenes_form_view_mode') || 'stacked');
  const [showVoiceNote, setShowVoiceNote] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [formRes, patientRes, aidesRes] = await Promise.all([
          formsApi.get(formId),
          patientApi.list(),
          // Aides directory — for the "Assigned Aide" dropdown. Fall back
          // to empty list if the caller doesn't have user-list perms (e.g. a
          // patient filling a form). Field is optional anyway.
          api.get('/admin/users', { params: { role: 'aide' } })
            .catch(() => ({ data: [] })),
        ]);
        setForm(formRes.data);
        setRawForm(formRes.data);
        setPatients(patientRes.data || []);
        const aidesList = Array.isArray(aidesRes.data)
          ? aidesRes.data
          : (aidesRes.data?.users || []);
        setAides(aidesList.filter(u => u.role === 'aide'));
        const initial = {};
        (formRes.data.fields || []).forEach(f => {
          initial[f.field_id] = f.field_type === 'checkbox' ? [] : '';
        });

        // For aides: if no patient is preselected, look up the aide's
        // currently-scheduled (or in-progress) visit for today and pre-pick
        // that patient so the form is ready to fill without an extra click.
        if (!preselectedPatientId && !preloadDraftId && user?.role === 'aide') {
          // Always stamp the current aide as the "Assigned Aide" — they're
          // the one filling the form, after all.
          setAideId(user.id);
          try {
            const { data: visits } = await api.get('/aide/my-visits');
            const sorted = (visits || []).slice().sort((a, b) => {
              const ord = { in_progress: 0, scheduled: 1, completed: 2, missed: 3, cancelled: 4 };
              return (ord[a.status] ?? 9) - (ord[b.status] ?? 9);
            });
            const pick = sorted.find(v => v.patient_id);
            if (pick?.patient_id) {
              setPatientId(pick.patient_id);
              if (pick.patient_name) {
                toast.success(`Auto-selected ${pick.patient_name} from your assignments`);
              }
            }
          } catch { /* non-fatal — aide can still pick manually */ }
        }

        // If we were resumed via ?draftId=…, hydrate responses and patient.
        if (preloadDraftId) {
          try {
            const { data: dr } = await formDraftApi.get(preloadDraftId);
            if (dr?.responses) {
              Object.assign(initial, dr.responses);
            }
            if (dr?.patient_id && !preselectedPatientId) {
              setPatientId(dr.patient_id);
            }
            if (dr?.updated_at) {
              setLastSavedAt(new Date(dr.updated_at));
            }
            setSaveStatus('saved');
          } catch {
            // Draft was likely already submitted or discarded; silently start fresh
            // and scrub the stale ?draftId from the URL so refreshes don't repeat the warning.
            toast('Starting a fresh form — the previous draft was no longer available.');
            setDraftId('');
            draftIdRef.current = '';
            const next = new URLSearchParams(searchParams);
            next.delete('draftId');
            setSearchParams(next, { replace: true });
          }
        }
        setResponses(initial);
      } catch (err) { toast.error('Failed to load form'); }
      finally { setLoading(false); }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, preloadDraftId, preselectedPatientId]);

  // Apply the current locale's translations on top of the raw form. Re-runs
  // whenever the user switches languages so the labels swap live. English is
  // the source of truth and never gets re-mapped.
  useEffect(() => {
    if (!rawForm) return;
    const lang = i18n.language || 'en';
    if (lang === 'en' || !rawForm.translations || !rawForm.translations[lang]) {
      setForm(rawForm);
      return;
    }
    const tx = rawForm.translations[lang];
    const fmap = tx.fields || {};
    const localized = {
      ...rawForm,
      name: tx.name || rawForm.name,
      description: tx.description || rawForm.description,
      fields: (rawForm.fields || []).map(f => {
        const t = fmap[f.field_id];
        if (!t) return f;
        return {
          ...f,
          label: t.label || f.label,
          placeholder: t.placeholder || f.placeholder,
          info_text: t.info_text || f.info_text,
          options: (Array.isArray(t.options) && t.options.length === (f.options || []).length)
            ? t.options
            : f.options,
        };
      }),
    };
    setForm(localized);
  }, [rawForm, i18n.language]);

  // Whenever a patient is selected, auto-fill any field with `patient_field_ref`
  // that the user hasn't already populated. Runs on patient change + form load.
  useEffect(() => {
    if (!form || !patientId) return;
    if (!(form.fields || []).some(f => f.patient_field_ref)) return;
    let cancelled = false;
    api.get(`/forms/${formId}/prefill`, { params: { patient_id: patientId } }).then(({ data }) => {
      if (cancelled) return;
      setRecordSchedule(data.schedule);
      if (data.interval_days) { const d = new Date(); d.setDate(d.getDate() + Number(data.interval_days)); setNextUpdateDue(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`); }
      const incoming = data.values || {};
      const previous = lastPrefillRef.current;
      setResponses(prev => {
        const next = { ...prev };
        // Clear values that came from the previously selected patient and weren't edited.
        Object.entries(previous).forEach(([fid, val]) => {
          if (next[fid] === val && !(fid in incoming)) delete next[fid];
        });
        Object.entries(incoming).forEach(([fid, val]) => {
          const current = next[fid];
          const isEmpty = current === '' || current == null || (Array.isArray(current) && current.length === 0);
          if (isEmpty || current === previous[fid]) next[fid] = val;
        });
        return next;
      });
      lastPrefillRef.current = incoming;
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [form, patientId, formId]);

  // When the patient changes (and the user isn't an aide themselves), look
  // up that patient's currently in-progress / scheduled aide assignment for
  // today and pre-fill the Assigned Aide dropdown. Optional — silent failure.
  useEffect(() => {
    if (!patientId || user?.role === 'aide') return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/care-logs/assignments`, {
          params: { patient_id: patientId, status: 'in_progress' },
        });
        if (cancelled) return;
        let pick = (Array.isArray(data) ? data : []).find(a => a.aide_id);
        if (!pick) {
          const { data: scheduled } = await api.get(`/care-logs/assignments`, {
            params: { patient_id: patientId, status: 'scheduled' },
          });
          pick = (Array.isArray(scheduled) ? scheduled : []).find(a => a.aide_id);
        }
        if (pick?.aide_id && !cancelled) setAideId(pick.aide_id);
      } catch { /* care-logs may not be enabled — non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [patientId, user?.role]);

  // Auto-save drafts while typing. Debounced 10s after the last mutation
  // — cheap enough for the server, infrequent enough for the user.
  useEffect(() => {
    if (loading || !form) return;
    // Skip the initial hydration pass (setting `responses` from `initial` or
    // from a resumed draft).
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }
    // Only auto-save if the user has typed something meaningful — avoids
    // creating empty draft rows for forms the user opens and immediately
    // navigates away from.
    const filled = Object.values(responses).filter(v => {
      if (v === null || v === undefined || v === '') return false;
      if (Array.isArray(v) && v.length === 0) return false;
      if (typeof v === 'object' && Object.keys(v).length === 0) return false;
      return true;
    });
    if (filled.length === 0) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('idle');
    saveTimerRef.current = setTimeout(async () => {
      try {
        setSaveStatus('saving');
        const { data } = await formDraftApi.save({
          form_id: formId,
          patient_id: patientId || null,
          responses,
          draft_id: draftIdRef.current || undefined,
        });
        if (data?.draft_id && !draftIdRef.current) {
          draftIdRef.current = data.draft_id;
          setDraftId(data.draft_id);
        }
        setLastSavedAt(new Date());
        setSaveStatus('saved');
      } catch {
        setSaveStatus('error');
      }
    }, 10000);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses, patientId]);

  // Flush any pending auto-save on page unload so the user doesn't lose
  // the last 10 seconds of typing when closing the tab.
  useEffect(() => {
    const handler = () => {
      if (saveTimerRef.current) {
        // best-effort — sendBeacon would be ideal but our API is JWT-auth'd.
        const filled = Object.values(responses).some(v => v && (typeof v !== 'object' || Object.keys(v).length > 0));
        if (filled) {
          try {
            formDraftApi.save({
              form_id: formId,
              patient_id: patientId || null,
              responses,
              draft_id: draftIdRef.current || undefined,
            });
          } catch { /* ignore */ }
        }
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses, patientId, formId]);

  const updateResponse = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
    // Mark linked task as in_progress on first edit (once per session)
    if (taskId && !taskMarkedInProgress) {
      setTaskMarkedInProgress(true);
      api.put(`/tasks/${taskId}`, { status: 'in_progress' }).catch(() => {});
    }
  };

  // Explicitly discard the current draft and navigate away. Called from the
  // header's "Discard" button so users can bail out of a half-filled form
  // without polluting the Forms In Progress widget.
  const handleDiscardDraft = async () => {
    const hasServerDraft = !!draftIdRef.current;
    const msg = hasServerDraft
      ? 'Discard this draft? Any unsubmitted changes will be lost.'
      : 'Discard changes and leave this form?';
    if (!window.confirm(msg)) return;
    if (hasServerDraft) {
      try {
        await formDraftApi.delete(draftIdRef.current);
        draftIdRef.current = '';
        setDraftId('');
      } catch {
        toast.error('Could not discard draft');
        return;
      }
    }
    // Cancel any pending auto-save so it doesn't resurrect the draft.
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    toast.success('Draft discarded');
    navigate(-1);
  };

  // Auto-compute `calculated` field values whenever any response changes.
  useEffect(() => {
    const calcFields = (form?.fields || []).filter(f => f.field_type === 'calculated' && f.formula);
    if (calcFields.length === 0) return;
    const updates = {};
    for (const cf of calcFields) {
      const result = evaluateFormula(cf.formula, { fields: responses });
      if (result !== null && responses[cf.field_id] !== result) {
        updates[cf.field_id] = result;
      }
    }
    if (Object.keys(updates).length > 0) {
      setResponses(prev => ({ ...prev, ...updates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses, form]);

  const toggleCheckbox = (fieldId, option) => {
    setResponses(prev => {
      const current = prev[fieldId] || [];
      return { ...prev, [fieldId]: current.includes(option) ? current.filter(o => o !== option) : [...current, option] };
    });
    if (taskId && !taskMarkedInProgress) {
      setTaskMarkedInProgress(true);
      api.put(`/tasks/${taskId}`, { status: 'in_progress' }).catch(() => {});
    }
  };

  const handleSubmit = async () => {
    if (!patientId) { toast.error('Please select a patient'); return; }
    for (const field of (form?.fields || [])) {
      if (field.required && field.field_type !== 'info') {
        const val = responses[field.field_id];
        if (!val || (Array.isArray(val) && val.length === 0)) {
          toast.error(`"${field.label}" is required`); return;
        }
      }
    }
    setSubmitting(true);
    try {
      const res = await submitFormResilient(api, formId, {
        form_id: formId,
        patient_id: patientId,
        aide_id: aideId || null,
        responses,
        form_name: form?.name,
        next_update_due: nextUpdateDue || null,
      });
      if (res?.data?.offline) {
        toast.success('Saved offline — will sync when you reconnect');
      } else {
        toast.success('Form submitted and saved to patient record');
        // Discard the now-unnecessary draft row so it disappears from the
        // Forms In Progress widget.
        if (draftIdRef.current) {
          try { await formDraftApi.delete(draftIdRef.current); } catch { /* non-blocking */ }
        }
      }
      // If this form was opened from a task, mark the task completed
      if (taskId) {
        try {
          await api.put(`/tasks/${taskId}`, { status: 'completed' });
        } catch { /* non-blocking */ }
      }
      navigate('/tasks');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Submission failed'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>;
  if (!form) return <div className="text-center py-20 text-[#64748B]">Form not found</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="form-fill-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>{form.name}</h1>
          {form.description && <p className="text-sm text-[#64748B]">{form.description}</p>}
        </div>
        {/* Auto-save indicator */}
        <div className="text-xs text-[#64748B] flex items-center gap-1.5 shrink-0" data-testid="autosave-indicator">
          {saveStatus === 'saving' && (<><Loader2 className="w-3 h-3 animate-spin text-[#0E7490]" /><span>Saving draft…</span></>)}
          {saveStatus === 'saved' && lastSavedAt && (<><Check className="w-3 h-3 text-[#166534]" /><span>Draft saved {lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span></>)}
          {saveStatus === 'error' && (<><CircleDashed className="w-3 h-3 text-[#991B1B]" /><span className="text-[#991B1B]">Draft save failed</span></>)}
          {saveStatus === 'idle' && draftId && (<><Check className="w-3 h-3 text-[#94A3B8]" /><span>Draft loaded</span></>)}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDiscardDraft}
          className="shrink-0 text-[#64748B] hover:text-[#991B1B] hover:bg-[#FEE2E2]"
          data-testid="discard-draft-btn"
          title="Discard this draft"
        >
          <Trash2 className="w-3.5 h-3.5 mr-1" />Discard
        </Button>
      </div>

      {/* Sign-on-PDF CTA — shown when the form was imported from a PDF with layout preserved */}
      {form.print_layout === 'pdf_original' && (
        <Card className="careiq-card border-l-4 border-l-[#0E7490] bg-[#F0FDFA]" data-testid="sign-on-pdf-cta">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center shrink-0">
                <Pen className="w-5 h-5 text-[#0E7490]" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#0A2342]">Tablet mode — Sign on PDF</div>
                <div className="text-xs text-[#64748B]">Fill fields and draw signatures directly on top of the original form. Great for iPads in the field.</div>
              </div>
            </div>
            <Button
              className="careiq-btn-primary flex items-center gap-2"
              onClick={() => {
                const params = new URLSearchParams();
                if (patientId) params.set('patientId', patientId);
                if (taskId) params.set('taskId', taskId);
                navigate(`/forms/${formId}/sign-on-pdf${params.toString() ? `?${params}` : ''}`);
              }}
              data-testid="open-sign-on-pdf-btn"
            >
              <Pen className="w-4 h-4" /> Open Sign-on-PDF
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Patient + Assigned Aide */}
      <Card className="careiq-card">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Select Patient *</Label>
            <Select value={patientId} onValueChange={setPatientId}>
              <SelectTrigger data-testid="patient-select"><SelectValue placeholder="Choose patient for this form" /></SelectTrigger>
              <SelectContent>
                {patients.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} (MRN: {p.mrn || p.id.slice(-6)})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="flex items-center gap-1">Assigned Aide <span className="text-[10px] text-[#94A3B8] font-normal">(optional · auto-filled)</span></Label>
            <Select value={aideId || 'none'} onValueChange={(v) => setAideId(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="aide-select"><SelectValue placeholder="No aide assigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No aide assigned</SelectItem>
                {aides.map(a => <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* View mode toggle — only when this form has a paper layout designed */}
      {form.paper_layout?.enabled && (
        <div className="flex items-center gap-2 justify-end" data-testid="form-view-mode-toggle">
          <span className="text-xs text-[#64748B]">View</span>
          <div className="flex bg-[#F1F5F9] rounded-lg p-0.5">
            <button
              onClick={() => { setViewMode('stacked'); localStorage.setItem('savenes_form_view_mode', 'stacked'); }}
              className={`px-3 py-1 rounded text-xs font-medium ${viewMode === 'stacked' ? 'bg-white shadow-sm text-[#0E7490]' : 'text-[#64748B]'}`}
              data-testid="view-stacked"
            >Stacked</button>
            <button
              onClick={() => { setViewMode('paper'); localStorage.setItem('savenes_form_view_mode', 'paper'); }}
              className={`px-3 py-1 rounded text-xs font-medium ${viewMode === 'paper' ? 'bg-white shadow-sm text-[#0E7490]' : 'text-[#64748B]'}`}
              data-testid="view-paper"
            >Paper</button>
          </div>
        </div>
      )}

      {/* Form Fields — Paper view when designed + selected */}
      {form.paper_layout?.enabled && viewMode === 'paper' ? (
        <PaperFillView
          form={form}
          responses={responses}
          setResponses={setResponses}
          signingFieldId={signingFieldId}
          setSigningFieldId={setSigningFieldId}
          zoom={0.85}
        />
      ) : (
      <div>
        <div className="flex flex-wrap gap-4 pr-2">
          {(form.fields || []).sort((a, b) => a.order - b.order).map((field, idx) => {
            const widthClass = field.width === 'half' ? 'w-full md:w-[calc(50%-8px)]' : field.width === 'third' ? 'w-full md:w-[calc(33.333%-11px)]' : 'w-full';
            return (
            <Card key={field.field_id} className={`careiq-card ${widthClass}`} data-testid={`fill-field-${idx}`}>
              <CardContent className="p-4">
                <Label className="flex items-center gap-1">
                  {field.label}
                  {field.required && <span className="text-[#DC2626]">*</span>}
                </Label>

                {field.field_type === 'text' && <Input value={responses[field.field_id] || ''} onChange={(e) => updateResponse(field.field_id, e.target.value)} placeholder={field.placeholder} data-testid={`input-${field.field_id}`} />}
                {field.field_type === 'number' && <Input type="number" value={responses[field.field_id] || ''} onChange={(e) => updateResponse(field.field_id, e.target.value)} placeholder={field.placeholder} />}
                {field.field_type === 'date' && <Input type="date" value={responses[field.field_id] || ''} onChange={(e) => updateResponse(field.field_id, e.target.value)} />}
                {field.field_type === 'email' && <Input type="email" value={responses[field.field_id] || ''} onChange={(e) => updateResponse(field.field_id, e.target.value)} placeholder={field.placeholder} />}
                {field.field_type === 'phone' && <Input type="tel" value={responses[field.field_id] || ''} onChange={(e) => updateResponse(field.field_id, e.target.value)} placeholder={field.placeholder} />}
                {field.field_type === 'textarea' && <Textarea value={responses[field.field_id] || ''} onChange={(e) => updateResponse(field.field_id, e.target.value)} placeholder={field.placeholder} className="min-h-[100px]" />}

                {field.field_type === 'select' && (
                  <Select value={responses[field.field_id] || ''} onValueChange={(v) => updateResponse(field.field_id, v)}>
                    <SelectTrigger><SelectValue placeholder={field.placeholder || 'Select...'} /></SelectTrigger>
                    <SelectContent>
                      {(field.options || []).map((opt, i) => <SelectItem key={i} value={opt}>{opt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}

                {field.field_type === 'radio' && (
                  <div className="space-y-2 mt-1">
                    {(field.options || []).map((opt, i) => (
                      <label key={i} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name={field.field_id} value={opt} checked={responses[field.field_id] === opt} onChange={() => updateResponse(field.field_id, opt)} className="text-[#0E7490]" />
                        <span className="text-sm text-[#0A2342]">{opt}</span>
                      </label>
                    ))}
                  </div>
                )}

                {field.field_type === 'checkbox' && (
                  <div className="space-y-2 mt-1">
                    {(field.options || []).map((opt, i) => (
                      <label key={i} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={(responses[field.field_id] || []).includes(opt)} onChange={() => toggleCheckbox(field.field_id, opt)} className="rounded" />
                        <span className="text-sm text-[#0A2342]">{opt}</span>
                      </label>
                    ))}
                  </div>
                )}

                {field.field_type === 'checkbox_text' && (
                  <div className="space-y-2 mt-1">
                    {(field.options || []).map((opt, i) => {
                      const val = responses[field.field_id] || {};
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <input type="checkbox" checked={!!val[`chk_${i}`]} onChange={(e) => updateResponse(field.field_id, { ...val, [`chk_${i}`]: e.target.checked })} className="rounded shrink-0" />
                          <span className="text-sm text-[#0A2342] shrink-0 min-w-[80px]">{opt}</span>
                          <Input value={val[`txt_${i}`] || ''} onChange={(e) => updateResponse(field.field_id, { ...val, [`txt_${i}`]: e.target.value })} placeholder="Details..." className="h-8 text-sm flex-1" disabled={!val[`chk_${i}`]} />
                        </div>
                      );
                    })}
                  </div>
                )}

                {field.field_type === 'form_reference' && field.reference_form_id && (
                  <div className="p-3 bg-[#0E7490]/5 rounded-lg text-sm text-[#0E7490]">
                    <FileText className="w-4 h-4 inline mr-1" />
                    References form: {form.referenced_forms?.[field.reference_form_id]?.name || field.reference_form_id}
                  </div>
                )}

                {field.field_type === 'info' && (
                  <div className="p-3 bg-[#F0FDFA] border border-[#14B8A6]/20 rounded-lg text-sm text-[#0A2342]" data-testid={`info-field-${field.field_id}`}>
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-[#14B8A6] mt-0.5 shrink-0" />
                      <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: field.info_text || field.label }} />
                    </div>
                  </div>
                )}

                {field.field_type === 'fill_blank' && (
                  <FillBlankField
                    template={field.fib_template || ''}
                    value={Array.isArray(responses[field.field_id]) ? responses[field.field_id] : []}
                    onChange={(arr) => updateResponse(field.field_id, arr)}
                    testIdPrefix={`fib-${field.field_id}`}
                  />
                )}

                {field.field_type === 'signature' && (
                  <div
                    className="border-2 border-dashed border-[#94A3B8] rounded-lg p-4 text-center cursor-pointer hover:border-[#0E7490] transition-colors"
                    onClick={() => setSigningFieldId(field.field_id)}
                    data-testid={`signature-${field.field_id}`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSigningFieldId(field.field_id); } }}
                  >
                    {responses[field.field_id] && typeof responses[field.field_id] === 'string' && responses[field.field_id].startsWith('data:image') ? (
                      <>
                        <img src={responses[field.field_id]} alt="Signature" className="max-h-24 mx-auto border border-[#E2E8F0] rounded bg-white" />
                        <div className="text-[10px] text-[#0E7490] mt-1">Tap to re-sign</div>
                      </>
                    ) : (
                      <div className="text-sm text-[#94A3B8] py-2">
                        <PenTool className="w-6 h-6 mx-auto mb-1" />
                        Tap to draw your signature
                      </div>
                    )}
                  </div>
                )}

                {field.field_type === 'section' && (
                  <div className="border-b-2 border-[#0E7490] pb-1" />
                )}

                {field.field_type === 'calculated' && (
                  <div data-testid={`calc-field-view-${field.field_id}`}>
                    <div className="mt-1 px-3 py-2 bg-[#EFF6FF] border border-[#2563EB]/20 rounded-lg font-mono text-base text-[#1E40AF]">
                      {formatComputed(evaluateFormula(field.formula || '', { fields: responses }), field.format || { type: 'number', decimals: 2 })}
                      <span className="text-[10px] text-[#64748B] ml-2">(auto-calculated)</span>
                    </div>
                  </div>
                )}

                {field.field_type === 'calc_table' && (
                  <CalcTableRenderer field={{ ...field, label: '' }} value={responses[field.field_id]} onChange={(v) => updateResponse(field.field_id, v)} outerFields={responses} />
                )}

                {field.field_type === 'checkbox_table' && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border border-[#E2E8F0] rounded-lg">
                      <thead><tr className="bg-[#F1F5F9]">
                        {(field.table_columns || ['Item']).map((col, ci) => <th key={ci} className="p-2 text-left text-xs font-medium text-[#64748B] border-b">{col}</th>)}
                      </tr></thead>
                      <tbody>
                        {(field.table_rows || ['Row 1']).map((row, ri) => (
                          <tr key={ri} className="border-b border-[#E2E8F0]">
                            <td className="p-2 text-xs font-medium text-[#0A2342]">{row}</td>
                            {(field.table_columns || []).slice(1).map((col, ci) => (
                              <td key={ci} className="p-2 text-center">
                                <input type="checkbox" checked={!!((responses[field.field_id] || {})[`${ri}_${ci}`])} onChange={(e) => {
                                  const tbl = { ...(responses[field.field_id] || {}) };
                                  tbl[`${ri}_${ci}`] = e.target.checked;
                                  updateResponse(field.field_id, tbl);
                                }} className="rounded" />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {field.field_type === 'multi_column' && (
                  <div>
                    <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${field.column_count || 2}, 1fr)` }}>
                      {(field.children || []).map((col, colIdx) => (
                        <div key={colIdx} className="space-y-3">
                          {(col || []).map((child, ci) => {
                            const childKey = `${field.field_id}_${colIdx}_${ci}`;
                            return (
                              <div key={ci} className="space-y-1">
                                {child.label && <Label>{child.label}</Label>}
                                {(child.field_type === 'text' || child.field_type === 'number' || child.field_type === 'email' || child.field_type === 'phone') && (
                                  <Input type={child.field_type === 'number' ? 'number' : 'text'} value={(responses[field.field_id] || {})[childKey] || ''} onChange={(e) => { const mc = { ...(responses[field.field_id] || {}), [childKey]: e.target.value }; updateResponse(field.field_id, mc); }} placeholder={child.placeholder} />
                                )}
                                {child.field_type === 'date' && <Input type="date" value={(responses[field.field_id] || {})[childKey] || ''} onChange={(e) => { const mc = { ...(responses[field.field_id] || {}), [childKey]: e.target.value }; updateResponse(field.field_id, mc); }} />}
                                {child.field_type === 'textarea' && <Textarea value={(responses[field.field_id] || {})[childKey] || ''} onChange={(e) => { const mc = { ...(responses[field.field_id] || {}), [childKey]: e.target.value }; updateResponse(field.field_id, mc); }} className="min-h-[60px]" />}
                                {child.field_type === 'select' && (
                                  <Select value={(responses[field.field_id] || {})[childKey] || ''} onValueChange={(v) => { const mc = { ...(responses[field.field_id] || {}), [childKey]: v }; updateResponse(field.field_id, mc); }}>
                                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                                    <SelectContent>{(child.options || []).map((o, oi) => <SelectItem key={oi} value={o}>{o}</SelectItem>)}</SelectContent>
                                  </Select>
                                )}
                                {child.field_type === 'checkbox' && (
                                  <div className="space-y-1">{(child.options || []).map((opt, oi) => (
                                    <label key={oi} className="flex items-center gap-2 cursor-pointer">
                                      <input type="checkbox" checked={((responses[field.field_id] || {})[childKey] || []).includes(opt)} onChange={() => { const mc = { ...(responses[field.field_id] || {}) }; const arr = [...(mc[childKey] || [])]; if (arr.includes(opt)) mc[childKey] = arr.filter(x => x !== opt); else { arr.push(opt); mc[childKey] = arr; } updateResponse(field.field_id, mc); }} className="rounded" />
                                      <span className="text-sm">{opt}</span>
                                    </label>
                                  ))}</div>
                                )}
                                {child.field_type === 'radio' && (
                                  <div className="space-y-1">{(child.options || []).map((opt, oi) => (
                                    <label key={oi} className="flex items-center gap-2 cursor-pointer">
                                      <input type="radio" name={childKey} checked={(responses[field.field_id] || {})[childKey] === opt} onChange={() => { const mc = { ...(responses[field.field_id] || {}), [childKey]: opt }; updateResponse(field.field_id, mc); }} />
                                      <span className="text-sm">{opt}</span>
                                    </label>
                                  ))}</div>
                                )}
                                {child.field_type === 'info' && <div className="p-2 bg-[#F0FDFA] rounded text-xs text-[#0A2342]">{child.info_text || child.label}</div>}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {field.field_type === 'reminder' && (
                  <div className="p-3 bg-[#FEF3C7]/50 border border-[#CA8A04]/20 rounded-lg text-sm" data-testid={`reminder-field-${field.field_id}`}>
                    <div className="flex items-center gap-2 text-[#92400E]">
                      <Clock className="w-4 h-4" />
                      <span>A {field.reminder_label || 'follow-up'} reminder will be created <strong>{field.reminder_days} day{field.reminder_days !== 1 ? 's' : ''}</strong> after submission</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      </div>
      )}

      <div className="flex justify-end items-center gap-2 flex-wrap">
        {recordSchedule && (
          <span className="text-[11px] text-[#64748B]" data-testid="fill-last-updated">Last updated {new Date(recordSchedule.last_updated_at).toLocaleString()} by {recordSchedule.last_updated_by_name}</span>
        )}
        {patientId && (
          <label className="flex items-center gap-1.5 text-xs text-[#64748B]">Next update due
            <Input type="date" value={nextUpdateDue} onChange={e => setNextUpdateDue(e.target.value)} className="h-9 w-[160px]" data-testid="fill-next-due-input" />
          </label>
        )}
        <Button variant="outline" onClick={() => setShowVoiceNote(true)} data-testid="form-voice-note-btn">
          <Mic className="w-4 h-4 mr-2" />Voice Note
        </Button>
        <Button className="careiq-btn-primary" onClick={handleSubmit} disabled={submitting} data-testid="submit-form-btn">
          <Send className="w-4 h-4 mr-2" />{submitting ? 'Submitting...' : 'Submit Form'}
        </Button>
      </div>

      <VoiceNoteDialog
        open={showVoiceNote}
        onOpenChange={setShowVoiceNote}
        patientId={patientId || null}
      />

      <SignaturePadDialog
        open={!!signingFieldId}
        onClose={() => setSigningFieldId(null)}
        onSave={(dataUrl) => {
          if (signingFieldId) {
            updateResponse(signingFieldId, dataUrl);
            toast.success('Signature captured');
          }
          setSigningFieldId(null);
        }}
        label={(form?.fields || []).find(f => f.field_id === signingFieldId)?.label || 'Signature'}
        initialValue={signingFieldId && typeof responses[signingFieldId] === 'string' && responses[signingFieldId].startsWith('data:image') ? responses[signingFieldId] : null}
      />
    </div>
  );
}
